import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Bot, BookOpenText, Copy, Eye, Lock, Play, Plus, Save, Sparkles, Wand2, X } from '@/components/icons';
import { STEP_KINDS, STEP_TEMPLATES, checkStepVariables, isBuiltin, testNameKey, type StepKind, type VariableProblem } from '@1qa/shared';
import { ApiError, api, errorMessage, type TestInput } from '@/lib/api';
import { effectiveVariables, problemsByStep, splitPlaceholders, variableProblemsFrom } from '@/lib/testData';
import type { InsertableVariable } from '@/features/builder/InsertVariableMenu';
import { useAuth } from '@/lib/auth';
import type { TestCase } from '@/lib/types';
import { describeStep, KIND_META } from '@/features/builder/describe';
import { KIND_ICONS, KIND_TONES, StepCard } from '@/features/builder/StepCard';
import { builderReducer, initialBuilderState, newStepId, scenarioProblems } from '@/features/builder/stepReducer';
import { PlatformIcon } from '@/components/PlatformIcon';
import { WatchLiveToggle } from '@/components/report/WatchLiveToggle';
import { liveToggleState, useWatchLivePreference } from '@/lib/liveView';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunBuild } from '@/lib/runBuild';
import { RunBuildPicker } from '@/components/builds/RunBuildPicker';
import { useRunDevice } from '@/lib/runDevice';
import { RunDevicePicker } from '@/components/builds/RunDevicePicker';
import { Badge, Button, Card, CardHeader, ErrorNotice, PageLoader, Textarea, useToast } from '@/components/ui';

/** Starter scenario so a blank page is never intimidating. (For mobile, "open" launches the app and the value is ignored.) */
function starterSteps() {
  return [
    { id: newStepId(), kind: 'open' as const, value: '/' },
    { id: newStepId(), kind: 'act' as const, value: '' },
    { id: newStepId(), kind: 'assert' as const, value: '' },
  ];
}

export function TestBuilderPage() {
  const { projectId = '', testId } = useParams();
  const isNew = !testId;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, canModify } = useAuth();

  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const [watchLive, setWatchLive] = useWatchLivePreference();
  const live = liveToggleState(project.data, watchLive);
  const runData = useRunCollection(projectId);
  const runBuild = useRunBuild(projectId, project.data?.platform);
  const runDevice = useRunDevice(projectId, project.data?.platform);
  const test = useQuery({ queryKey: ['test', testId], queryFn: () => api.test(testId!), enabled: !isNew });
  const variables = useQuery({ queryKey: ['project-variables', projectId], queryFn: () => api.projectVariables(projectId) });
  // Other tests in the project: names must be unique (the API enforces it too).
  const siblings = useQuery({ queryKey: ['tests', projectId], queryFn: () => api.tests(projectId) });
  const llm = useQuery({ queryKey: ['llm-status', projectId], queryFn: () => api.llmStatus(projectId), staleTime: 60_000, retry: false });
  const [serverProblems, setServerProblems] = useState<VariableProblem[]>([]);

  const [state, dispatch] = useReducer(builderReducer, undefined, () => initialBuilderState());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [metaDirty, setMetaDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const loadedFor = useRef<string | null>(null);

  // Drag & drop state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [over, setOver] = useState<{ index: number; pos: 'before' | 'after' } | null>(null);

  const orgId = project.data?.orgId ?? null;
  const canWrite = can('test.write', orgId);
  // Only the creator (or a Project Admin) changes a test; everyone else who can write tests copies it.
  const readOnly = isNew ? !canWrite : !canModify('test.write', test.data?.createdById, orgId);
  const othersTest = !isNew && !!test.data && readOnly && canWrite;
  const canRun = can('run.trigger', orgId);

  const copy = useMutation({
    mutationFn: () => api.copyTest(testId!),
    onSuccess: (t) => {
      toast.success(`“${t.name}” created - it's yours to edit`);
      qc.invalidateQueries({ queryKey: ['tests', projectId] });
      navigate(`/projects/${projectId}/tests/${t.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  // Initialise the editor once per test (or once for a new test once the project is known).
  useEffect(() => {
    const key = isNew ? `new:${projectId}` : (test.data?.id ?? null);
    if (!key || loadedFor.current === key) return;
    if (isNew && !project.data) return;
    loadedFor.current = key;
    if (isNew) {
      dispatch({ type: 'load', steps: starterSteps() });
      setName('');
      setDescription('');
      setTags([]);
    } else if (test.data) {
      dispatch({ type: 'load', steps: test.data.steps });
      setName(test.data.name);
      setDescription(test.data.description ?? '');
      setTags(test.data.tags ?? []);
    }
    setMetaDirty(false);
  }, [isNew, projectId, project.data, test.data]);

  const dirty = state.dirty || metaDirty;

  // Test data: what can be inserted, and live validation of {{...}} references.
  const available = useMemo(() => (variables.data ? effectiveVariables(variables.data) : null), [variables.data]);
  const insertable = useMemo<InsertableVariable[]>(
    () => {
      // One entry per name, even when Dev, Staging... each have their own value.
      const byKey = new Map<string, InsertableVariable>();
      for (const v of variables.data ?? []) {
        if (v.overridden) continue;
        const prev = byKey.get(v.key);
        if (!prev || (prev.scope === 'ORG' && v.scope === 'PROJECT')) {
          byKey.set(v.key, { key: v.key, isSecret: available?.get(v.key) ?? v.isSecret, scope: v.scope, description: prev?.description ?? v.description });
        }
      }
      return [...byKey.values()];
    },
    [variables.data, available],
  );
  const liveVariableProblems = useMemo(() => (available ? checkStepVariables(state.steps, available) : []), [available, state.steps]);
  const variableMessages = useMemo(() => {
    const live = problemsByStep(liveVariableProblems);
    // Server-side problems (e.g. a variable deleted meanwhile) stay until the next save attempt.
    for (const [id, msgs] of problemsByStep(serverProblems)) live.set(id, [...new Set([...(live.get(id) ?? []), ...msgs])]);
    return live;
  }, [liveVariableProblems, serverProblems]);

  const nameTaken = useMemo(
    () => !!name.trim() && (siblings.data ?? []).some((t) => t.id !== testId && testNameKey(t.name) === testNameKey(name)),
    [name, siblings.data, testId],
  );
  const nameTakenMessage = `A test named “${name.trim()}” already exists in this project. Choose a different name.`;

  const problems = useMemo(
    () => [
      ...(name.trim() ? [] : ['Give your test a name.']),
      ...(nameTaken ? [nameTakenMessage] : []),
      ...scenarioProblems(state.steps),
      ...liveVariableProblems.map((p) => p.message),
    ],
    [name, nameTaken, nameTakenMessage, state.steps, liveVariableProblems],
  );

  const usesAi = state.steps.some((s) => s.kind === 'act' || s.kind === 'assert');
  const showAiBanner = usesAi && llm.data?.configured === false;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: async (): Promise<TestCase> => {
      setServerProblems([]);
      const body: TestInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        type: 'FUNCTIONAL',
        steps: state.steps.map((s) => ({ ...s, value: s.value.trim() })),
        tags,
      };
      return isNew ? api.createTest(projectId, body) : api.updateTest(testId!, body);
    },
    onSuccess: (saved) => {
      dispatch({ type: 'saved' });
      setMetaDirty(false);
      qc.setQueryData(['test', saved.id], saved);
      qc.invalidateQueries({ queryKey: ['tests', projectId] });
      if (isNew) {
        loadedFor.current = saved.id;
        navigate(`/projects/${projectId}/tests/${saved.id}`, { replace: true });
      }
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'INVALID_VARIABLES') {
        setServerProblems(variableProblemsFrom(e.details));
        setShowErrors(true);
      }
      // Someone else took the name meanwhile: refresh the list so the name field shows it.
      if (e instanceof ApiError && e.code === 'TEST_NAME_TAKEN') qc.invalidateQueries({ queryKey: ['tests', projectId] });
    },
  });

  const runNow = useMutation({
    mutationFn: async () => {
      const saved = dirty || isNew ? await save.mutateAsync() : test.data!;
      return api.triggerRun(projectId, [saved.id], { liveView: live.checked, collectionId: runData.collectionId, appBuildId: runBuild.appBuildId, deviceProfile: runDevice.deviceProfile });
    },
    onSuccess: (run) => {
      toast.success('Test saved and started');
      navigate(`/runs/${run.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const trySave = useCallback(() => {
    if (problems.length) {
      setShowErrors(true);
      toast.error(problems[0]);
      return false;
    }
    save.mutate(undefined, {
      onSuccess: () => toast.success('Test saved'),
      onError: (e) => toast.error(errorMessage(e)),
    });
    return true;
  }, [problems, save, toast]);

  const tryRun = () => {
    if (problems.length) {
      setShowErrors(true);
      toast.error(problems[0]);
      return;
    }
    runNow.mutate();
  };

  // Ctrl/Cmd + S saves.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        trySave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, trySave]);

  const onDragOver = useCallback(
    (index: number, e: DragEvent) => {
      if (dragIndex === null) return;
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
      setOver((o) => (o?.index === index && o.pos === pos ? o : { index, pos }));
    },
    [dragIndex],
  );

  const onDrop = useCallback(
    (index: number) => {
      if (dragIndex === null || !over) return;
      let to = over.pos === 'before' ? index : index + 1;
      if (dragIndex < to) to -= 1;
      dispatch({ type: 'reorder', from: dragIndex, to });
      setDragIndex(null);
      setOver(null);
    },
    [dragIndex, over],
  );

  const onDragEnd = useCallback(() => {
    setDragIndex(null);
    setOver(null);
  }, []);

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (t && !tags.includes(t) && tags.length < 10) {
      setTags([...tags, t]);
      setMetaDirty(true);
    }
    setTagDraft('');
  };

  if (project.isPending || (!isNew && test.isPending)) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  if (test.isError) return <ErrorNotice error={test.error} onRetry={() => test.refetch()} />;

  const p = project.data;

  return (
    <div className="pb-28">
      <Link to={`/projects/${p.id}`} className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="size-4" /> {p.name}
      </Link>

      <div className="mt-4 mb-8 flex flex-wrap items-start gap-4 animate-slide-up">
        <PlatformIcon platform={p.platform} size="lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <label htmlFor="test-name" className="sr-only">
            Test name
          </label>
          <input
            id="test-name"
            value={name}
            readOnly={readOnly}
            onChange={(e) => {
              setName(e.target.value);
              setMetaDirty(true);
            }}
            placeholder="Name your test, e.g. “Customer can sign in”"
            maxLength={120}
            aria-invalid={nameTaken || (showErrors && !name.trim())}
            aria-describedby={nameTaken ? 'test-name-taken' : undefined}
            className={clsx(
              'w-full rounded-field bg-transparent px-2 py-1 -ml-2 text-2xl font-semibold tracking-[-0.04em] text-fg ring-inset placeholder:text-fg-muted focus:bg-surface-sunken focus:shadow-focus focus:ring-1 focus:ring-primary focus:outline-none sm:text-[32px] sm:leading-10',
              'aria-invalid:shadow-focus-danger aria-invalid:ring-1 aria-invalid:ring-danger-text',
            )}
          />
          {nameTaken && !readOnly && (
            <p id="test-name-taken" role="alert" className="text-sm text-danger-text">
              {nameTakenMessage}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="brand" icon={<Sparkles className="size-3" />}>
              Functional test
            </Badge>
            {tags.map((t) => (
              <Badge key={t}>
                {t}
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={`Remove tag ${t}`}
                    onClick={() => {
                      setTags(tags.filter((x) => x !== t));
                      setMetaDirty(true);
                    }}
                    className="-mr-1 rounded-full hover:text-danger-text"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </Badge>
            ))}
            {!readOnly && (
              <input
                aria-label="Add tag"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                onBlur={addTag}
                placeholder="+ add tag"
                className="w-24 rounded-full bg-transparent px-3 py-[5px] text-[11px]/[16.5px] text-fg-secondary ring-1 ring-border ring-inset placeholder:text-fg-muted focus:ring-primary focus:outline-none"
              />
            )}
          </div>
        </div>
      </div>

      {othersTest && (
        <div role="status" className="mb-6 flex flex-wrap items-center gap-3 rounded-card bg-surface-sunken p-4 text-sm text-fg-secondary ring-1 ring-border ring-inset">
          <Lock className="size-4 shrink-0 text-fg-muted" />
          <p className="min-w-0 flex-1">
            <span className="font-medium">{test.data?.createdBy?.name ?? 'Someone else'} created this test.</span> Only they or a Project Admin can
            change it. Make a copy to work on your own version.
          </p>
          <Button variant="outline" onClick={() => copy.mutate()} loading={copy.isPending} icon={<Copy className="size-4" />}>
            Make a copy
          </Button>
        </div>
      )}

      {showAiBanner && (
        <div role="status" className="mb-6 flex flex-wrap items-start gap-3 rounded-card bg-warning-subtle p-4 text-sm text-fg ring-1 ring-warning/40 ring-inset">
          <Bot className="mt-0.5 size-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">A Project Admin needs to connect an AI model before this test can run.</p>
            <p className="mt-0.5 text-fg-secondary">
              “Do something” and “Check something” steps are carried out by an AI model. You can keep writing and saving the test in the meantime.
            </p>
          </div>
          {can('llm.manage', orgId) && (
            <Link to={`/ai-models?project=${encodeURIComponent(projectId)}`} className="font-medium text-warning underline underline-offset-2 hover:no-underline">
              Set up an AI model
            </Link>
          )}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section aria-labelledby="steps-heading" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 id="steps-heading" className="text-xs font-semibold tracking-label text-fg-muted uppercase">
              Steps
            </h2>
            <span className="text-xs text-fg-muted tabular-nums">{state.steps.length} / 200</span>
          </div>

          <Textarea
            aria-label="What does this test check?"
            readOnly={readOnly}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setMetaDirty(true);
            }}
            placeholder="Optional: what does this test check? e.g. “A returning customer can sign in and sees their orders.”"
            rows={2}
          />

          <ol className="space-y-3">
            {state.steps.map((step, i) => (
              <StepCard
                key={step.id}
                step={step}
                index={i}
                total={state.steps.length}
                platform={p.platform}
                readOnly={readOnly}
                showErrors={showErrors}
                dispatch={dispatch}
                dragging={dragIndex === i}
                dropTarget={over?.index === i && dragIndex !== null && dragIndex !== i ? over.pos : null}
                onDragStart={setDragIndex}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                variables={insertable}
                variableMessages={variableMessages.get(step.id)}
              />
            ))}
          </ol>

          {!readOnly && (
            <div className="rounded-card border border-dashed border-border p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-label text-fg-secondary uppercase">
                <Plus className="size-4" /> Add a step
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STEP_KINDS.map((k: StepKind) => {
                  const Icon = KIND_ICONS[k];
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => dispatch({ type: 'add', kind: k })}
                      className={clsx('flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold tracking-label uppercase ring-1 ring-inset transition hover:brightness-125', KIND_TONES[k])}
                    >
                      <Icon className="size-4" /> {KIND_META[k].label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          {!readOnly && (
            <Card>
              <CardHeader title={<span className="inline-flex items-center gap-2"><Wand2 className="size-3.5 text-primary" /> Quick add</span>} description="Common steps - click to add, then edit the text." />
              <div className="flex flex-wrap gap-2 p-4">
                {STEP_TEMPLATES.map((t) => {
                  const Icon = KIND_ICONS[t.kind];
                  return (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => dispatch({ type: 'add', kind: t.kind, value: t.value })}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[11px]/[16.5px] font-semibold tracking-label uppercase bg-surface text-fg ring-1 ring-border ring-inset transition hover:bg-primary-subtle hover:text-primary hover:ring-primary"
                    >
                      <Icon className="size-3.5" /> {t.label}
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title={<span className="inline-flex items-center gap-2"><Eye className="size-3.5 text-primary" /> Readable preview</span>} description="This is what 1QA will do, in order." />
            <ol className="space-y-2.5 p-5 text-sm" aria-live="polite">
              {state.steps.map((s, i) => (
                <li key={s.id} className="flex gap-3">
                  <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-primary-subtle font-display text-[10px] text-primary">{i + 1}</span>
                  <span className={clsx(s.value.trim() || s.kind === 'open' ? 'text-fg' : 'text-fg-muted italic')}>
                    {splitPlaceholders(describeStep(s, p.platform)).map((seg, j) =>
                      seg.placeholder ? (
                        <mark
                          key={j}
                          className={clsx(
                            'rounded-bar px-1 font-mono text-[12px]',
                            !isBuiltin(seg.placeholder) && available && !available.has(seg.placeholder)
                              ? seg.written !== undefined
                                ? 'bg-surface-raised text-fg'
                                : 'bg-danger-subtle text-danger-text'
                              : available?.get(seg.placeholder)
                                ? 'bg-warning-subtle text-warning'
                                : 'bg-primary-subtle text-primary',
                          )}
                          title={
                            seg.written !== undefined
                              ? available?.has(seg.placeholder)
                                ? `Uses the test data ${seg.placeholder}; “${seg.written}” is used when a run's test data has no ${seg.placeholder}.`
                                : `No test data has ${seg.placeholder} yet, so runs use the written value “${seg.written}”.`
                              : undefined
                          }
                        >
                          {seg.text}
                        </mark>
                      ) : (
                        <span key={j}>{seg.text}</span>
                      ),
                    )}
                  </span>
                </li>
              ))}
              {state.steps.length === 0 && <li className="text-fg-muted">No steps yet.</li>}
            </ol>
          </Card>

          <Card>
            <div className="p-5 text-sm">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-label text-primary uppercase">
                <BookOpenText className="size-3.5" /> Tips for great tests
              </div>
              <ul className="list-disc space-y-1.5 pl-5 text-fg-secondary marker:text-primary">
                <li>Write steps the way you would explain them to a new colleague.</li>
                <li>Mention the visible text of buttons and fields, e.g. the “Checkout” button.</li>
                <li>End with a “Check something” step - that is what decides pass or fail.</li>
                <li>Keep one goal per test. Short tests are faster and easier to fix.</li>
              </ul>
            </div>
          </Card>
        </aside>
      </div>

      {(!readOnly || (canRun && !isNew)) && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/90 backdrop-blur-lg lg:left-[240px]">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-10">
            {readOnly ? (
              <span className="mr-auto text-sm text-fg-muted">You can run this test; make a copy to change it.</span>
            ) : (
              <>
                <span className="flex items-center gap-2 text-sm text-fg-muted" aria-live="polite">
                  <span className={clsx('size-2 rounded-full', dirty ? 'bg-warning' : 'bg-success')} />
                  {dirty ? 'Unsaved changes' : isNew ? 'New test' : 'All changes saved'}
                  {showErrors && problems.length > 0 && <span className="text-danger-text">· {problems.length} to fix</span>}
                </span>
                <span className="ml-auto hidden text-xs text-fg-muted md:block">Ctrl + S to save</span>
                <Button variant="secondary" onClick={trySave} loading={save.isPending && !runNow.isPending} disabled={!dirty && !isNew} icon={<Save className="size-4" />}>
                  Save
                </Button>
              </>
            )}
            {canRun && <RunBuildPicker state={runBuild} className="hidden sm:inline-flex" />}
            {canRun && <RunDevicePicker state={runDevice} className="hidden sm:inline-flex" />}
            {canRun && <RunCollectionPicker state={runData} className="hidden sm:inline-flex" />}
            {canRun && <WatchLiveToggle state={live} onChange={setWatchLive} className="hidden sm:inline-flex" />}
            {canRun && (
              <Button onClick={tryRun} loading={runNow.isPending} icon={<Play className="size-4" />}>
                {dirty || isNew ? 'Save & run now' : 'Run now'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
